import { cloneDeep } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { CommandCenterHousehold } from '@ndvr/customer-api';
import { Account, AccountGroup, LifecycleStatus } from '@ndvr/portfolio-api';

import { includesIgnoreCase } from 'utils';
import { useCommandCenterHouseholds } from 'views/household/household-queries';
import { useListAccountGroupsByLifecycleStatus } from 'views/portfolio/account-group-queries';
import { useListAccountsByLifecycleStatus } from 'views/portfolio/account-queries';
import { FormLabel, IconHouse, SearchInput, Text, VerticalLayout } from 'widgets';
import { CustodyId } from 'widgets/CustodyId';

import { HighlightText } from '../../widgets/HighlightText';

export function AccountSelectorTree({
    accountField,
    portfolioField,
    householdField,
}: {
    accountField: string;
    portfolioField?: string;
    householdField?: string;
}) {
    const { setValue } = useFormContext();

    const { data: households, isPending: isHouseholdsPending } = useCommandCenterHouseholds();
    const { data: accountGroups, isPending: isAccountGroupsPending } = useListAccountGroupsByLifecycleStatus([
        LifecycleStatus.REAL,
    ]);
    const { data: accounts, isPending: isAccountsPending } = useListAccountsByLifecycleStatus([LifecycleStatus.REAL]);

    // Filter households to only include those with at least one accountGroup
    const filteredHouseholds = useMemo(() => {
        if (!households || !accountGroups) return [];

        // Create a set of householdGUIDs that have at least one accountGroup
        const householdGUIDsWithAccountGroups = new Set(
            accountGroups.map((accountGroup) => accountGroup.householdGUID),
        );

        // Filter households to only include those in the set
        return households.filter((household) => householdGUIDsWithAccountGroups.has(household.globalAccountGUID));
    }, [households, accountGroups]);

    // Get child account groups for a household
    const getAccountGroups = useCallback(
        (householdGUID: string) => {
            if (!accountGroups) return [];
            return accountGroups.filter((group) => group.householdGUID === householdGUID);
        },
        [accountGroups],
    );

    // Get child accounts for an account group
    const getAccounts = useCallback(
        (accountGroupGUID: string) => {
            if (!accounts) return [];
            return accounts.filter((account) => account.accountGroupGUID === accountGroupGUID);
        },
        [accounts],
    );

    // Handle account selection from TreeSelector, set form value to selected account GUID
    const handleAccountSelect = useCallback(
        (account: Account | null, portfolio: AccountGroup | null, household: CommandCenterHousehold | null) => {
            accountField && setValue(accountField, account?.accountGUID);
            portfolioField && setValue(portfolioField, portfolio?.accountGroupGUID);
            householdField && setValue(householdField, household?.globalAccountGUID);
        },
        [setValue, accountField, portfolioField, householdField],
    );

    const householdNodes: HouseholdNode[] = filteredHouseholds.map((household) => {
        const childGroups: PortfolioNode[] = getAccountGroups(household.globalAccountGUID).map((group) => {
            const childAccounts: AccountNode[] = getAccounts(group.accountGroupGUID).map((account) => {
                return {
                    id: account.accountGUID,
                    label: account.accountName?.plainText || 'Unnamed Account',
                    data: account,
                    children: [],
                };
            });
            return {
                id: group.accountGroupGUID,
                label: group.accountGroupName?.plainText || 'Unnamed Portfolio',
                data: group,
                children: childAccounts,
            };
        });
        return {
            id: household.globalAccountGUID,
            label: household.globalAccountNamePlain || 'Unnamed Household',
            data: household,
            children: childGroups,
        };
    });

    const isLoading = isHouseholdsPending || isAccountGroupsPending || isAccountsPending;

    return (
        <TreeSelector
            householdNodes={householdNodes}
            isLoading={isLoading}
            onSelect={handleAccountSelect}
            filter={(node, search) =>
                includesIgnoreCase(node.label, search) ||
                includesIgnoreCase(node.id, search) ||
                ('custodyAccountId' in (node.data as any) &&
                    typeof (node.data as Account).custodyAccountId === 'string' &&
                    includesIgnoreCase((node.data as Account).custodyAccountId, search))
            }
        />
    );
}

export interface TreeNode {
    id: string;
    label: string;
    data: unknown;
    children: TreeNode[];
}
interface HouseholdNode extends TreeNode {
    data: CommandCenterHousehold;
    children: PortfolioNode[];
}
interface PortfolioNode extends TreeNode {
    data: AccountGroup;
    children: AccountNode[];
}
interface AccountNode extends TreeNode {
    data: Account;
    children: [];
}

/**
 * Filter the tree nodes by id or label match.
 */
export function filterTreeNodes<T extends TreeNode>(
    nodes: T[],
    search: string,
    filter: (node: T, search: string) => boolean,
): T[] {
    // empty list by default
    if (!search || search.length < 2) return [];
    return nodes.map((node) => filterNode(node, search, filter)).filter((node): node is T => node !== null);
}

/**
 * Recursively filters a single node or returns null.
 * If the node matches, includes all children.
 * Else, includes only matching descendants.
 */
function filterNode<T extends TreeNode>(
    node: T,
    search: string,
    filter: (node: T, search: string) => boolean,
): T | null {
    const matches = filter(node, search);

    if (matches) {
        // Node matches: include entire subtree
        return {
            ...node,
            children: node.children.map((child) => cloneDeep(child)),
        };
    }

    // Node doesn’t match: check children
    const matchedChildren = filterTreeNodes(node.children as T[], search, filter);
    if (matchedChildren.length > 0) {
        return { ...node, children: matchedChildren };
    }

    return null;
}

function TreeSelector({
    householdNodes,
    isLoading = false,
    filter = (node: TreeNode, search: string) => node.label.includes(search) || node.id.includes(search),
    onSelect,
    width = '500px',
}: {
    householdNodes: HouseholdNode[];
    isLoading?: boolean;
    filter?: (node: TreeNode, search: string) => boolean;
    onSelect?: (
        account: Account | null,
        portfolio: AccountGroup | null,
        household: CommandCenterHousehold | null,
    ) => void;
    width?: string | number;
}) {
    const [selected, setSelected] = useState<{
        account: Account;
        portfolio: AccountGroup;
        household: CommandCenterHousehold;
    } | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Split search term into words
    const searchWords = useMemo(() => {
        return searchTerm
            .toLowerCase()
            .trim()
            .split(/\s+/)
            .filter((word) => word.length >= 2);
    }, [searchTerm]);

    const filteredNodes = useMemo(
        () => filterTreeNodes(householdNodes, searchTerm, filter).sort((a, b) => a.label.localeCompare(b.label)),
        [householdNodes, searchTerm, filter],
    );

    const handleSelect = useCallback(
        (accountNode: AccountNode, portfolioNode: PortfolioNode, householdNode: HouseholdNode) => {
            setSelected({ account: accountNode.data, portfolio: portfolioNode.data, household: householdNode.data });
            setSearchTerm('');
            onSelect?.(accountNode.data, portfolioNode.data, householdNode.data);
        },
        [onSelect, setSelected, setSearchTerm],
    );

    const handleClearSelection = useCallback(() => {
        setSelected(null);
        setSearchTerm('');
        onSelect?.(null, null, null);
    }, [onSelect, setSelected, setSearchTerm]);

    // Show "type more characters" message when search term is too short
    const showTypeMoreMessage = searchTerm.length > 0 && searchWords.length === 0;

    return (
        <VerticalLayout $gap={16} style={{ width }}>
            {!selected && (
                <>
                    <FormLabel>Select Account</FormLabel>

                    <SearchInput
                        placeholder="Search for households, account groups, or accounts..."
                        value={searchTerm}
                        onChange={(ev) => setSearchTerm(ev.target.value)}
                        autoFocus={searchTerm.length > 0}
                        loading={isLoading}
                    />

                    <Text helper $themeColor="subtle">
                        Search by name or ID...
                    </Text>
                </>
            )}

            {selected ? (
                <SelectedResult {...selected} onClearSelection={handleClearSelection} />
            ) : showTypeMoreMessage ? (
                <TypeMoreNotification />
            ) : filteredNodes.length > 0 ? (
                <ResultTree householdNodes={filteredNodes} searchTerm={searchTerm} onSelect={handleSelect} />
            ) : searchTerm.length >= 3 ? (
                <NoResult />
            ) : null}
        </VerticalLayout>
    );
}

const SelectedResult = ({
    account,
    portfolio,
    household,
    onClearSelection,
}: {
    account: Account;
    portfolio: AccountGroup;
    household: CommandCenterHousehold;
    onClearSelection: () => void;
}) => {
    return (
        <SelectedResultWrapper>
            <ClearSelectionWrapper onClick={onClearSelection}>×</ClearSelectionWrapper>
            <Text>Household</Text>
            <Text>{household?.globalAccountNamePlain || 'Unnamed Household'}</Text>
            <Text>Portfolio</Text>
            <Text>{portfolio?.accountGroupName?.plainText || 'Unnamed Portfolio'}</Text>
            <Text>Account</Text>
            <Text>{account?.accountName.plainText || 'Unnamed Account'}</Text>
            <Text></Text>
            <Text>
                Brokerage: {account?.custodyType}&nbsp; ID: <CustodyId value={account?.custodyAccountId} />
            </Text>
        </SelectedResultWrapper>
    );
};

const ResultTree = ({
    householdNodes,
    searchTerm,
    onSelect,
}: {
    householdNodes: HouseholdNode[];
    searchTerm: string;
    onSelect: (accountNode: AccountNode, portfolioNode: PortfolioNode, householdNode: HouseholdNode) => void;
}) => {
    return (
        <TreeResults>
            <VerticalLayout $gap={8} $height="100%">
                {householdNodes.map((householdNode) => (
                    <TreeNodeView key={`h_${householdNode.id}`}>
                        <TreeNodeHeader>
                            <IconHouse size="micro" style={{ display: 'inline', margin: '0 4px 0 0' }} />
                            <HighlightText text={householdNode.label} searchTerm={searchTerm} />
                        </TreeNodeHeader>

                        {householdNode.children.length > 0 && (
                            <TreeNodeChildren>
                                {householdNode.children.map((portfolioNode) => (
                                    <TreeNodeView key={`p_${portfolioNode.id}`}>
                                        <TreeNodeHeader>
                                            <HighlightText text={portfolioNode.label} searchTerm={searchTerm} />
                                        </TreeNodeHeader>

                                        {portfolioNode.children.length > 0 && (
                                            <TreeNodeChildren>
                                                {portfolioNode.children.map((accountNode) => (
                                                    <TreeLeafItem
                                                        key={`a_${accountNode.id}`}
                                                        onClick={() =>
                                                            onSelect(accountNode, portfolioNode, householdNode)
                                                        }
                                                    >
                                                        &gt;{' '}
                                                        <HighlightText
                                                            text={accountNode.label}
                                                            searchTerm={searchTerm}
                                                        />
                                                    </TreeLeafItem>
                                                ))}
                                            </TreeNodeChildren>
                                        )}
                                    </TreeNodeView>
                                ))}
                            </TreeNodeChildren>
                        )}
                    </TreeNodeView>
                ))}
            </VerticalLayout>
        </TreeResults>
    );
};

const TypeMoreNotification = () => (
    <VerticalLayout $padding="4px 8px">
        <Text $italic $themeColor="subtle">
            Please type at least 2 characters to search
        </Text>
    </VerticalLayout>
);

const NoResult = () => (
    <VerticalLayout $padding="4px 8px">
        <Text $italic $themeColor="subtle">
            No results found
        </Text>
    </VerticalLayout>
);

const ClearSelectionWrapper = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    padding: 0.5rem 1rem;
    cursor: pointer;
    &:hover {
        color: ${({ theme }) => theme.colors.cocoa600};
    }
`;

const SelectedResultWrapper = styled.div`
    border: 1px solid ${({ theme }) => theme.border.light};
    border-radius: 3px;
    padding: 1rem;
    display: grid;
    grid-template-columns: 1fr 3fr;
    position: relative;
`;

const TreeResults = styled.div`
    overflow-y: scroll;
    max-height: 100%;
    padding: 8px;
    display: flex;
    flex-direction: column;
    border: 1px solid ${({ theme }) => theme.border.light};
    border-radius: 3px;
`;

const TreeNodeView = styled.div`
    display: flex;
    flex-direction: column;
`;

const TreeNodeHeader = styled.div`
    padding: 4px 8px;
`;

const TreeNodeChildren = styled.div`
    margin-left: 16px;
    border-left: 1px solid ${({ theme }) => theme.border.light};
    padding-left: 8px;
`;

const TreeLeafItem = styled.div`
    padding: 4px 8px;
    cursor: pointer;
    background-color: ${({ theme }) => theme.colors.cocoa0};

    &:hover {
        background-color: ${({ theme }) => theme.colors.cocoa100};
    }
`;
